import { FocusEvent, Fragment, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Field, FieldArray, FieldArrayRenderProps, FormikProvider, useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { isEqual, isNumber, keys, last, noop, omit, set, sortBy, uniqueId } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import Grid from '@mui/material/Grid';
import { Box, CircularProgress, Fab } from '@mui/material';
import { KeyboardBackspaceRounded, WarningRounded } from '@mui/icons-material';
import { AxiosError } from 'axios';
import moment, { Moment } from 'moment';

// Components
import DatePicker from 'components/DatePicker/DatePicker';
import InputBlock from 'components/global/inputBlock/inputBlock';
import Button from 'components/Button/Button';
import { FormikCheckboxConditionalNumberInput } from 'components/forms/FormikCheckboxConditionalNumberInput';
import { CertifyingBoardForm } from '../CertifyingBoardForm';
import { FormikTextField } from 'components/ContinuousImprovement/FormikTextField';
import { ITypeaheadOption, Typeahead } from 'components/ContinuousImprovement/Typeahead';
import { HighlightBox } from 'components/HighlightBox';
import { LoadingCards } from 'components/LoadingCard';

// Store + Core
import { activityDetailSelector, activityTypesWithCreditLimitSelector } from 'store/activity/selectors';
import { getActivity } from 'store/activity/actions';
import {
  isLearnerCompletionAddedSelector,
  isLearnerMatchingSelector,
  learnerValidationExceptionSelector,
  matchedLearnersSelector,
  previousMatchQuerySelector,
} from 'store/learner/selectors';
import {
  addLearnerCompletion,
  getRecentLearnerCompletions,
  matchLearners,
  resetLearnerMatches,
} from 'store/learner/actions';
import { statesAndProvincesSelector } from 'store/locations/selectors';
import { amaCreditTermIdSelector } from 'store/taxonomy/selectors';
import {
  Activity,
  IAddLearnersFormModel,
  ICertifyingBoardFormModel,
  IDictionary,
  IFormInputOption,
  ILearnerCompletion,
  ILearnerMatchingModel,
  IMatchedLearner,
  IStateAndProvince,
  ITaxonomyTerm,
  PARSAction,
} from 'core/models';
import { ButtonVariant, StatusEnum } from 'core/enums';
import {
  BoardLearnerCollectionFields,
  BoardTypes,
  CollectionTypes,
  IBCTBoard,
  IBCTBoardLearnerCollectionField,
  StatusTypes,
} from 'layouts/pages/bct/types';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'store';

// Styles
import styles from './AddLearnerForm.module.scss';

// Misc
import {
  getMatchPayloadFromFormValues,
  handleAddLearnerServerError,
  ICreditTypeFormOption,
  IFieldError,
  isNpiFormat,
  transformAddLearnerFormSubmitValues,
} from 'layouts/pages/learner/add-learner/utils';
import { buildAddLearnerSchema } from '../validationSchemas';
import { learnerMatched } from './utils/learnerMatched';

interface IProps {
  boardConfigs: IDictionary<IBCTBoard>;
  boardCreditOptions: IDictionary<ICreditTypeFormOption[]>;
  deaRegistrationOptions: IFormInputOption[];
  isLoadingForm: boolean;
  medicalSchoolOptions: ITypeaheadOption[];
  mocBoardList: IFormInputOption[];
  practiceAreaOptions: IFormInputOption[];
  professionOptions: IFormInputOption[];
  remsIds: string[];
  remsList: IFormInputOption[];
  specialtyBoardIds: string[];
  timeInPracticeOptions: IFormInputOption[];
}

interface IFieldSearch {
  boardId: string;
  fieldType: BoardLearnerCollectionFields;
}

enum SubmitIntent {
  SubmitAndClose = 'SubmitAndClose',
  SubmitAndContinue = 'SubmitAndContinue',
}

export const initialCertifyingBoardValues: ICertifyingBoardFormModel = {
  boardId: '',
  certifyingBoard: '',
  deaRegistration: '',
  id: '',
  learnerCredits: {},
  practiceArea: '',
  practiceState: '',
  prescribedInPast12Months: undefined,
  profession: '',
  providerLearnerId: '',
  surgicalProcedures: undefined,
  timeInPractice: '',
  totalCredits: 0,
};

const initialValues: IAddLearnersFormModel = {
  certifyingBoards: [],
  completionDate: undefined,
  credits: {
    cmeCredit: '' as any, // Required since `undefined` creates uncontrolled input error and 0 is not desired as visual default
    creditscmeCredit: false, // Default to unchecked
  },
  dateOfBirth: undefined,
  firstName: '',
  lastName: '',
  learnerId: '',
  learnerIdAlt: '',
  licenseId: '',
  medicalSchoolOption: undefined,
  npi: '',
  stateName: undefined,
};

// Create a new entry in the Certifying Boards array with a uniqueId
const generateUniqueCertifyingBoardEntry = (): ICertifyingBoardFormModel => ({
  ...initialCertifyingBoardValues,
  id: uniqueId('board_'),
});

// TODO: The 'visible' & 'required' fields throughout the form may be dependent on the Board type selected
// TODO: Add logic to control visibility with BCT configuration (1886 & 1888)
export const AddLearnerForm = (props: IProps): ReactElement => {
  const { boardConfigs, medicalSchoolOptions, mocBoardList, specialtyBoardIds, remsIds, remsList, boardCreditOptions } =
    props;

  // Local State.
  const [isAutoFillCompleted, setIsAutoFillCompleted] = useState<boolean>(false);
  const [submitIntent, setSubmitIntent] = useState<SubmitIntent>();
  const [certifyingBoardMatches, setCertifyingBoardMatches] = useState<IDictionary<boolean>>({});
  const [isCertifyingBoardMatchedWithLearner, setIsCertifyingBoardMatchedWithLearner] = useState<boolean>();
  const hasAttemptedRetryRef = useRef<boolean>(false);

  // Hooks.
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  const dispatch: ThunkDispatch<AppState, unknown, PARSAction> = useDispatch();

  // Selectors.
  const activity: Activity = useSelector(activityDetailSelector);
  const previousMatchQuery: ILearnerMatchingModel = useSelector(previousMatchQuerySelector);
  const isMatchingInProgress: boolean = useSelector(isLearnerMatchingSelector);
  const matchedLearners: IMatchedLearner[] = useSelector(matchedLearnersSelector);
  const isLearnerCompletionAdded: boolean = useSelector(isLearnerCompletionAddedSelector);
  const statesAndProvinces: IStateAndProvince[] = useSelector(statesAndProvincesSelector);
  const maxCreditActivityTypes: string[] = useSelector(activityTypesWithCreditLimitSelector);
  const amaCreditTerm: ITaxonomyTerm = useSelector(amaCreditTermIdSelector);
  const validationExceptions: AxiosError = useSelector(learnerValidationExceptionSelector);
  const activityType: string = activity?.typeId;
  const isMaxCreditsLimited: boolean = maxCreditActivityTypes?.includes(activityType);
  const hasOneEntry: boolean = matchedLearners?.length === 1;

  const credits = activity?.credits;
  const statesAndProvincesOptions: ITypeaheadOption[] =
    statesAndProvinces?.map(
      ({ isoStateCode, stateName }: IStateAndProvince): ITypeaheadOption => ({
        id: isoStateCode,
        label: stateName,
      }),
    ) || [];

  const sortCredits = useMemo(
    () =>
      (creditsKey: string): number =>
        credits?.[creditsKey],
    [credits],
  );
  const highestCreditKey: string = last(sortBy(keys(credits), sortCredits));
  const allowsCme: boolean = keys(activity?.credits).includes(amaCreditTerm?.id);

  const formik = useFormik<IAddLearnersFormModel>({
    initialValues,
    onSubmit: async (values: IAddLearnersFormModel): Promise<void> => {
      const allSelectedBoardIds: string[] = values?.certifyingBoards?.map(({ certifyingBoard }) => certifyingBoard);
      const isAllBoardsRems: boolean =
        !!allSelectedBoardIds?.length &&
        allSelectedBoardIds?.every((id: string): boolean => boardConfigs?.[id]?.type === BoardTypes.REMS);

      if (matchedLearners?.length !== 1 && !isAllBoardsRems) {
        return;
      }

      const universalLearnerId: string = matchedLearners?.[0]?.ulid;
      const activityCreditKeys: string[] = keys(activity?.credits);
      const amaCreditId: string = amaCreditTerm?.id;
      const cmeCreditId: string = activityCreditKeys.includes(amaCreditId) ? amaCreditId : activityCreditKeys?.[0];

      const submitValues: ILearnerCompletion = transformAddLearnerFormSubmitValues({
        addLearnersFormModel: values,
        creditId: cmeCreditId,
        nonRemsBoardIds: specialtyBoardIds,
        remsBoardIds: remsIds,
        universalLearnerId,
      });
      await dispatch(addLearnerCompletion(submitValues, activityId));
    },
    validateOnChange: true,
    validationSchema:
      activityId === activity?.id
        ? buildAddLearnerSchema({
            boardActivityDetails: activity?.boardMocDetails,
            boardConfigs,
            creditMaxClaimDate: activity?.mocCreditDeadline,
            endDate: activity?.endDate,
            isLearnerMatched: hasOneEntry || isCertifyingBoardMatchedWithLearner === true,
            isMaxCreditsLimited,
            isMoc: activity?.isMoc,
            maxTotalCredits: highestCreditKey ? credits?.[highestCreditKey] : 0,
            startDate: activity?.startDate,
            amaCreditTerm,
          })
        : undefined,
  });

  const {
    errors,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    resetForm,
    setFieldError,
    setFieldValue,
    submitForm,
    touched,
    validateForm,
    values,
  } = formik;

  // When determining if we can submit the form, we need to know if any of the dynamically generated Certifying Board
  // Forms have returned false for their match. If they have, then the submit button needs to be disabled.
  useEffect(() => {
    const selectedMocBoards = values?.certifyingBoards?.filter((cb) => {
      const boardConfig = boardConfigs?.[cb.certifyingBoard];
      return !!cb.certifyingBoard && boardConfig?.type !== BoardTypes.REMS;
    });

    const areAllBoardsMatched =
      !selectedMocBoards || selectedMocBoards.length === 0
        ? undefined
        : selectedMocBoards.every((b) => certifyingBoardMatches[b.id] === true);

    if (areAllBoardsMatched !== isCertifyingBoardMatchedWithLearner) {
      setIsCertifyingBoardMatchedWithLearner(areAllBoardsMatched);
    }
  }, [values?.certifyingBoards, certifyingBoardMatches, isCertifyingBoardMatchedWithLearner, boardConfigs]);

  // Custom error handling to work with the add learner form model.
  useEffect(() => {
    if (validationExceptions) {
      const fieldErrors: IFieldError[] = handleAddLearnerServerError(validationExceptions, values);

      fieldErrors?.forEach(({ formikKey, message }: IFieldError): void => setFieldError(formikKey, message));
    }
  }, [setFieldError, validationExceptions]);

  const hasError = !!keys(errors).length;

  // If a learner is matched but the learner doesn't belong to any selected certifying boards, disable submit
  const allSelectedBoardIds: string[] = values?.certifyingBoards?.map(
    ({ certifyingBoard }: ICertifyingBoardFormModel): string => certifyingBoard,
  );

  const selectedMocBoards: string[] = allSelectedBoardIds?.filter((id: string): boolean => {
    const config: IBCTBoard = boardConfigs?.[id];

    return config?.type === BoardTypes.CERTIFYING_BOARD;
  });

  const { isMatched, stateLicenseId, matchedStateOption, mocLearnerIds } = learnerMatched({
    matchedLearners,
    selectedMocBoardIds: selectedMocBoards,
    stateName: values?.stateName,
    statesAndProvincesOptions,
  });

  const hasMultipleStates: boolean = useMemo(() => {
    if (matchedLearners?.length !== 1) return false;

    const { learnerStateBoards } = matchedLearners[0];

    return !!learnerStateBoards.length;
  }, [matchedLearners]);

  useEffect(() => {
    if (activity?.id !== activityId) {
      dispatch(getActivity(activityId));
    }
  }, [dispatch, activity, activityId]);

  // If there's a match for 1 learner AND all boards, enable submit CTAs
  useEffect(() => {
    // Exactly 1 learner match is a success
    if (hasOneEntry && !isAutoFillCompleted) {
      // We are not done with autofill until we mark as true
      setIsAutoFillCompleted(true);

      const { medicalSchoolName } = matchedLearners[0];
      const matchedLearnerMedicalSchoolOption: ITypeaheadOption = medicalSchoolOptions?.find(
        ({ label }: ITypeaheadOption): boolean => label === medicalSchoolName,
      );

      const matchedData = {
        dateOfBirth: matchedLearners[0].dateOfBirth,
        lastName: matchedLearners[0].lastName,
        licenseId: matchedLearners[0].learnerStateBoards[0]?.learnerId,
        medicalSchoolOption: matchedLearnerMedicalSchoolOption,
        npi: matchedLearners[0].npi,
        stateName: matchedStateOption,
        firstName: matchedLearners[0].firstName,
      };

      const matchedLearnersKeptValues = Object.fromEntries(Object.entries(matchedData).filter(([, value]) => !!value));

      // keep the non-falsey user-entered values even if the match has a different value
      const keptValues = Object.fromEntries(Object.entries(values).filter(([, value]) => !!value));

      const updatedFormValues: IAddLearnersFormModel = {
        ...keptValues,
        ...matchedLearnersKeptValues,
        learnerIdAlt: '',
      };

      // go through the boards already selected and fill in learner IDs of the person we just matched
      if (updatedFormValues.certifyingBoards && mocLearnerIds) {
        updatedFormValues.certifyingBoards = updatedFormValues.certifyingBoards.map((i) => {
          if (i.certifyingBoard && mocLearnerIds[i.certifyingBoard] && (!i.boardId || i.boardId === '0')) {
            return { ...i, boardId: mocLearnerIds[i.certifyingBoard] };
          }
          return i;
        });
      }

      // Reset the form with values from the matched learner synchronously
      resetForm({ errors, touched, values: updatedFormValues });

      // Trigger validation with the newly matched learner values
      validateForm(updatedFormValues);
    }
  }, [
    errors,
    hasOneEntry,
    isAutoFillCompleted,
    matchedLearners,
    matchedStateOption,
    medicalSchoolOptions,
    mocLearnerIds,
    resetForm,
    stateLicenseId,
    touched,
    validateForm,
    values,
    values.certifyingBoards,
  ]);

  const getIsRequired = useMemo(
    () =>
      ({ boardId, fieldType }: IFieldSearch): boolean =>
        !!boardConfigs[boardId]?.boardLearnerCollectionFields?.find(
          ({ fieldType: fieldName, collectionType }: IBCTBoardLearnerCollectionField): boolean =>
            fieldName === fieldType && collectionType === CollectionTypes.REQUIRED,
        ),
    [boardConfigs],
  );

  // If we have only one entry, set the state license Id to the field for the user.
  useEffect(() => {
    if (stateLicenseId) {
      setFieldValue('licenseId', stateLicenseId);
    }
  }, [setFieldValue, stateLicenseId]);

  const searchIsRequired = useMemo(
    () =>
      (fieldType: BoardLearnerCollectionFields): boolean =>
        !!values?.certifyingBoards?.find(({ certifyingBoard }: ICertifyingBoardFormModel): boolean =>
          getIsRequired({ boardId: certifyingBoard, fieldType }),
        ),
    [values?.certifyingBoards, getIsRequired],
  );

  // Submission Sequencing Hook
  useEffect(() => {
    // If completion was added and there's no errors
    if (isLearnerCompletionAdded && !hasError && !isSubmitting) {
      // If the intent was to Submit and Close
      if (submitIntent === SubmitIntent.SubmitAndClose) {
        navigate(`/learners/summary/detail/${activityId}`);
      }

      // If the intent was to Submit and Add Another Learner
      if (submitIntent === SubmitIntent.SubmitAndContinue) {
        // After submitting, get the latest completions so the UI is the most up-to-date
        dispatch(getRecentLearnerCompletions(activityId));
        resetForm({ errors, touched, values: initialValues });
        hasAttemptedRetryRef.current = false;
      }
      dispatch(resetLearnerMatches());
      setSubmitIntent(null);
    }
  }, [submitIntent, hasError, isLearnerCompletionAdded, isSubmitting]);

  // Reset matched learners on unmount.
  useEffect(() => {
    return () => {
      dispatch(resetLearnerMatches());
      hasAttemptedRetryRef.current = false;
    };
  }, [dispatch]);

  // Automatic retry with licenseId when no matches found
  // deps intentionally exclude values.learnerId and hasAttemptedRetryRef to avoid firing on every keystroke
  useEffect(() => {
    const shouldTriggerRetry =
      matchedLearners?.length === 0 &&
      !hasAttemptedRetryRef.current &&
      !isMatchingInProgress &&
      !!values.learnerId &&
      !!previousMatchQuery;

    if (shouldTriggerRetry) {
      hasAttemptedRetryRef.current = true;
      setFieldValue('licenseId', '');
      const retryPayload = getMatchPayloadFromFormValues(values, boardConfigs);
      // Retry with the opposite field type from the initial attempt
      const modifiedPayload = isNpiFormat(values.learnerId)
        ? { ...retryPayload, licenseId: values.learnerId, npi: '' }
        : { ...retryPayload, npi: values.learnerId, licenseId: '' };
      dispatch(matchLearners(modifiedPayload));
    }
  }, [matchedLearners, isMatchingInProgress, previousMatchQuery]);

  // Reset retry state when key form fields change
  useEffect(() => {
    hasAttemptedRetryRef.current = false;
  }, [values.firstName, values.lastName, values.dateOfBirth, values.stateName, values.learnerId]);

  // Capture to disable add boards if only reporting for CME
  const activityIsMoc: boolean = activity?.isMoc;
  const activityIsRems: boolean = activity?.isRems;
  const isActivityOnlyCme: boolean = !activityIsMoc && !activityIsRems;

  // Input Option for Learner credits (NOTE: these are distinct from MOC credits)
  const cmeCreditOption: IFormInputOption = { Id: 'cmeCredit', Title: 'Report for CME credit' };
  const isCmeCreditsChecked = !!values?.credits?.creditscmeCredit;
  const certifyingBoardIds = (values?.certifyingBoards || [])
    .filter(({ certifyingBoard }) => certifyingBoard && boardConfigs?.[certifyingBoard]?.type !== BoardTypes.REMS)
    .map(({ certifyingBoard }) => certifyingBoard)
    .sort()
    .join('__');

  // If firstName, lastName, dateOfBirth, and stateName are filled in but no learner matched, learnerId (npi field) is required
  const { dateOfBirth, firstName, lastName, stateName } = values;

  const isLearnerIdRequiredToMatch: boolean = !!dateOfBirth && !!firstName && !!lastName && !!stateName && !isMatched;
  const hasBoardIdLearnerMatch: boolean =
    isCertifyingBoardMatchedWithLearner === true &&
    values?.certifyingBoards?.some(({ boardId }: ICertifyingBoardFormModel): boolean => !!boardId);

  // If all fields, including license ID are filled and no match exists, show supplemental fields
  const isAllMatchFieldsFailed: boolean = isLearnerIdRequiredToMatch && !hasBoardIdLearnerMatch;

  const { COMPLETION_DATE, DATE_OF_BIRTH, FIRST_NAME, LAST_NAME, LICENSE_ID, STATE_OF_LICENSURE } =
    BoardLearnerCollectionFields;

  const isCompletionDateRequired: boolean = searchIsRequired(COMPLETION_DATE) || isCmeCreditsChecked;
  const isDobRequired: boolean = searchIsRequired(DATE_OF_BIRTH) || isCmeCreditsChecked;
  const isFirstNameRequired: boolean = searchIsRequired(FIRST_NAME) || isCmeCreditsChecked;
  const isLastNameRequired: boolean = searchIsRequired(LAST_NAME) || isCmeCreditsChecked;
  const isLicensedStateRequired: boolean =
    !hasBoardIdLearnerMatch && (searchIsRequired(STATE_OF_LICENSURE) || isCmeCreditsChecked || hasMultipleStates);
  const isLearnerIdRequired: boolean =
    !hasBoardIdLearnerMatch || searchIsRequired(LICENSE_ID) || isLearnerIdRequiredToMatch;

  useEffect(() => {
    setFieldValue('learnerIdAlt', '');
  }, [isAllMatchFieldsFailed, setFieldValue]);

  const handleBaseLearnerMatching = async (
    learnerFormValues: IAddLearnersFormModel,
    forceRunMatch = false,
  ): Promise<void> => {
    // Clear the hidden licenseId before building the payload so stale programmatic values don't
    // interfere and so the user-entered learnerId flows through as licenseId correctly.
    const matchingPayload: ILearnerMatchingModel = getMatchPayloadFromFormValues(
      { ...learnerFormValues, licenseId: '' },
      boardConfigs,
    );

    // If matching is not in transit and the match query hasn't changed, trigger a match request
    if (forceRunMatch || (!isMatchingInProgress && !isEqual(previousMatchQuery, matchingPayload))) {
      if (!isAllMatchFieldsFailed) {
        setFieldValue('licenseId', '');
        await dispatch(matchLearners(omit(matchingPayload, ['medicalSchoolName'])));
      } else {
        await dispatch(matchLearners(matchingPayload));
      }
    }
  };

  const onBlurLearnerField = (event: FocusEvent<HTMLInputElement>): void => {
    handleBaseLearnerMatching(values).then(noop);
    handleBlur(event);
  };

  // fire match when is-cme changes, or when the list of certifying boards does, but only once we've already started matching
  useEffect(() => {
    if (previousMatchQuery) {
      handleBaseLearnerMatching(values).then(noop);
    }
  }, [isCmeCreditsChecked, certifyingBoardIds]);

  const onBlurTypeaheadField = (name: string, value: ITypeaheadOption): void => {
    const matchPayload = set(values, name, value);
    handleBaseLearnerMatching(matchPayload).then(noop);

    if (name === 'state') {
      // Clear out the npi fields whenever the state name changes.
      setFieldValue('learnerId', '');
      setFieldValue('learnerIdAlt', '');
    }
  };

  const submitAndClose = async (): Promise<void> => {
    setSubmitIntent(SubmitIntent.SubmitAndClose);
    await submitForm();
  };

  const submitAndAddAnother = async (): Promise<void> => {
    setSubmitIntent(SubmitIntent.SubmitAndContinue);
    await submitForm();
  };

  const closeWithoutSaving = (): void => {
    // Before leaving the page, clear learner match history from state
    dispatch(resetLearnerMatches());
    navigate(-1);
  };

  // Filter selected REMS and MOC Boards before passing down
  const filterBoardsById = useMemo(
    () =>
      ({ Id }: IFormInputOption): boolean =>
        !values?.certifyingBoards?.find(
          ({ certifyingBoard }: ICertifyingBoardFormModel): boolean => certifyingBoard === Id,
        ),
    [values?.certifyingBoards],
  );
  const filterBoardsActiveForLearners = ({ Id }: IFormInputOption): boolean =>
    boardConfigs?.[Id]?.learnerStatus === StatusTypes.ACTIVE;

  const filterMocBoardHaveAllData = ({ Id }: IFormInputOption): boolean => {
    const board = activity?.boardMocDetails?.[Id];
    return (
      board &&
      activity?.mocCreditDeadline &&
      isNumber(board.mocPointsGiven) &&
      board.mocPointsGiven > 0 &&
      board.practiceIds?.length > 0 &&
      board.typesOfCreditIds?.length > 0
    );
  };

  const filteredMocList: IFormInputOption[] = mocBoardList
    ?.filter(filterBoardsById)
    ?.filter(filterBoardsActiveForLearners)
    ?.filter(filterMocBoardHaveAllData);
  const filteredRemsList: IFormInputOption[] = remsList
    ?.filter(filterBoardsById)
    ?.filter(filterBoardsActiveForLearners);

  // If there's no boards/collabs to select, disable the add options
  const isDisabledAddBoards: boolean = !filteredMocList?.length && !filteredRemsList?.length;

  const isAllBoardsRems: boolean =
    !!allSelectedBoardIds?.length &&
    allSelectedBoardIds?.every((id: string): boolean => boardConfigs?.[id]?.type === BoardTypes.REMS);

  /**
   * Submission rules:
   * - No boards selected: allow submit when the form is valid and exactly one learner is matched.
   * - MOC/certifying boards selected: require the learner and every selected certifying board to match.
   * - REMS-only selected: allow REMS to be submitted with a matched learner and valid entries in the inputs.
   */
  const selectedBoardIds: string[] = allSelectedBoardIds.filter(Boolean);
  const hasSelectedBoards: boolean = selectedBoardIds.length > 0;
  const hasSelectedMocBoards: boolean = selectedMocBoards.length > 0;
  const hasMatchedLearner: boolean = matchedLearners?.length === 1;

  const checkIfBoardMatchLearner = (): boolean => {
    if (!hasMatchedLearner || !selectedBoardIds.length) return false;

    const selectedNonRemsBoardIds = selectedBoardIds.filter(
      (id: string): boolean => !remsList.some((remsOption) => remsOption.Id === id),
    );

    if (!selectedNonRemsBoardIds.length) return false;
    if (isCertifyingBoardMatchedWithLearner === false) return true;

    const learnerBoards = new Set(matchedLearners[0].learnerMocBoards.map(({ boardId }) => boardId));

    return selectedNonRemsBoardIds.some((id: string): boolean => !learnerBoards.has(id));
  };

  const hasBoardMismatch: boolean = checkIfBoardMatchLearner();

  const canSubmitWithoutBoards: boolean = isValid && hasMatchedLearner && !hasSelectedBoards && !isSubmitting;

  const canSubmitWithMocBoards: boolean =
    isValid &&
    hasMatchedLearner &&
    hasSelectedMocBoards &&
    isCertifyingBoardMatchedWithLearner === true &&
    !hasBoardMismatch &&
    !isSubmitting;

  const isRemsSubmitValid: boolean = isValid && isAllBoardsRems && !isSubmitting;

  const shouldDisableSubmit: boolean = !(canSubmitWithoutBoards || canSubmitWithMocBoards || isRemsSubmitValid);

  const filteredBoardCreditOptions: IDictionary<IFormInputOption[]> = useMemo(() => {
    if (!values.completionDate) return boardCreditOptions;
    const completionDate = moment(values.completionDate);
    if (!completionDate.isValid) return boardCreditOptions;

    const result: IDictionary<IFormInputOption[]> = {};
    for (const key in boardCreditOptions) {
      result[key] = boardCreditOptions[key].filter(
        ({ learnerCompletionStartDate, learnerCompletionEndDate }: ICreditTypeFormOption) => {
          const configuredStartDate: Moment = learnerCompletionStartDate && moment(learnerCompletionStartDate);
          const configuredEndDate: Moment = learnerCompletionEndDate && moment(learnerCompletionEndDate);

          if (configuredStartDate && configuredStartDate.isAfter(completionDate, 'day')) return false;
          if (configuredEndDate && configuredEndDate.isBefore(completionDate, 'day')) return false;

          return true;
        },
      );
    }
    return result;
  }, [values.completionDate, boardCreditOptions]);

  // If the activity hasn't loaded yet, render a loading indicator
  if (!activity) return <LoadingCards count={2} />;

  // If the activity is still in DRAFT status, don't render the form
  if (activity?.status === StatusEnum.DRAFT) {
    return (
      <HighlightBox variant="danger">
        <Box component="p" sx={{ display: 'flex' }}>
          <WarningRounded />
          <Box sx={{ ml: 2 }}>Learners cannot be added to this activity at this time</Box>
        </Box>
      </HighlightBox>
    );
  }

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <div className="static-form">
          <div className="form-group">
            <h5>Enter identification information and CME credits for the learner. Add a board to report MOC.</h5>
            <Grid container spacing={2} className={styles['form-grid']}>
              <Grid className={styles.input} size={{ xs: 12, sm: 3 }}>
                <InputBlock
                  errors={errors}
                  name="firstName"
                  required={isFirstNameRequired}
                  suppressLabel
                  touched={touched}
                  title="First name"
                >
                  <FormikTextField
                    formikKey="firstName"
                    helperText=""
                    onBlur={onBlurLearnerField}
                    placeholder="First name"
                    required={isFirstNameRequired}
                    variant="outlined"
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ xs: 12, sm: 3 }}>
                <InputBlock
                  errors={errors}
                  name="lastName"
                  required={isLastNameRequired}
                  touched={touched}
                  suppressLabel
                  title="Last name"
                >
                  <FormikTextField
                    formikKey="lastName"
                    helperText=""
                    onBlur={onBlurLearnerField}
                    placeholder="Last name"
                    required={isLastNameRequired}
                    variant="outlined"
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ xs: 12, sm: 2 }}>
                <InputBlock
                  errors={errors}
                  name="dateOfBirth"
                  touched={touched}
                  title="Dob"
                  required={isDobRequired}
                  suppressLabel
                >
                  <Field
                    component={DatePicker}
                    helperText=""
                    id="dateOfBirth"
                    name="dateOfBirth"
                    className="form-input"
                    format="MM/DD"
                    onBlur={onBlurLearnerField}
                    disablePast={false}
                    required={isDobRequired}
                    timeOfDay="midnight"
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ xs: 12, sm: 4 }}>
                <InputBlock
                  errors={errors}
                  name="completionDate"
                  touched={touched}
                  title="Date completed"
                  required={isCompletionDateRequired}
                  suppressLabel
                >
                  <Field
                    component={DatePicker}
                    helperText=""
                    id="completionDate"
                    name="completionDate"
                    className="form-input"
                    onBlur={onBlurLearnerField}
                    disablePast={false}
                    required={isCompletionDateRequired}
                    timeOfDay="midnight"
                  />
                </InputBlock>
              </Grid>
              {allowsCme && (
                <Grid size={{ xs: 12 }}>
                  <InputBlock errors={errors} name="credits" required touched={touched}>
                    <FormikCheckboxConditionalNumberInput
                      helperText=""
                      option={cmeCreditOption}
                      isRequired
                      formikKey="credits"
                      numberInputLabel="total credits"
                      variant="outlined"
                      required={isCmeCreditsChecked}
                    />
                  </InputBlock>
                </Grid>
              )}
            </Grid>
            <h5>Enter State and NPI (Preferred) or State License ID</h5>
            <Grid container spacing={2} className={styles['form-grid']}>
              <Grid className={styles.input} size={{ xs: 12, sm: 4 }}>
                <InputBlock
                  errors={errors}
                  name="stateName"
                  touched={touched}
                  title="Licensing state"
                  required={isLicensedStateRequired}
                  suppressLabel
                >
                  <Typeahead
                    options={statesAndProvincesOptions}
                    onChange={onBlurTypeaheadField}
                    id="stateName"
                    name="stateName"
                    placeholder="Select a state or province"
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ xs: 12, sm: 4 }}>
                <InputBlock
                  errors={errors}
                  touched={touched}
                  name="learnerId"
                  title="Learner Identification Number"
                  required={isLearnerIdRequired}
                  suppressLabel
                  tooltipContent="This can be a NPI or Licensing ID"
                >
                  <FormikTextField
                    aria-required={isLearnerIdRequired}
                    formikKey="learnerId"
                    helperText=""
                    onBlur={onBlurLearnerField}
                    placeholder="NPI or Licensing ID"
                    required={isLearnerIdRequired}
                    variant="outlined"
                  />
                </InputBlock>
              </Grid>
              {isMatched && (
                <Grid size={{ xs: 12 }}>
                  <div className={styles['pill-box']}>
                    <span className={classNames(styles['match-pill'], { [styles['match-pill--success']]: isMatched })}>
                      Learner Matched
                    </span>
                  </div>
                </Grid>
              )}
              {hasMultipleStates && !formik.values.stateName && !isCertifyingBoardMatchedWithLearner ? (
                <Grid size={{ xs: 12 }}>
                  <div className={classNames('caption-text', styles['error-text'])}>
                    Select a state or province to continue.
                  </div>
                </Grid>
              ) : (
                <Grid size={{ xs: 12 }}>
                  <div className={classNames('caption-text', styles['error-text'])}>
                    <span>&nbsp;</span>
                  </div>
                </Grid>
              )}
              {isAllMatchFieldsFailed && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <div className={styles['pill-box']}>
                      <span className={classNames(styles['match-pill'], { [styles['match-pill--fail']]: !isMatched })}>
                        Learner Not Matched
                      </span>
                    </div>
                    <div className={classNames('caption-text', styles['error-text'])}>
                      Provide additional information below to match this learner.
                    </div>
                  </Grid>
                  <Grid className={styles.input} size={{ xs: 12, sm: 4 }}>
                    <InputBlock
                      errors={errors}
                      touched={touched}
                      name="learnerIdAlt"
                      title="Alternate Learner Identification Number"
                      required={isLearnerIdRequired}
                      suppressLabel
                      tooltipContent="Please enter a different NPI or a different Licensing ID"
                    >
                      <FormikTextField
                        aria-required={isLearnerIdRequired}
                        formikKey="learnerIdAlt"
                        helperText=""
                        onBlur={onBlurLearnerField}
                        placeholder="NPI or Licensing ID"
                        required={isLearnerIdRequired}
                        variant="outlined"
                      />
                    </InputBlock>
                  </Grid>
                  <Grid className={styles.input} size={{ xs: 12, sm: 4 }}>
                    <InputBlock
                      errors={errors}
                      touched={touched}
                      name="medicalSchoolOption"
                      title="Medical school"
                      suppressLabel
                    >
                      <Typeahead
                        id="medicalSchoolOption"
                        name="medicalSchoolOption"
                        onChange={onBlurTypeaheadField}
                        options={medicalSchoolOptions}
                        placeholder="Select a medical school"
                      />
                    </InputBlock>
                  </Grid>
                </>
              )}
            </Grid>
            <hr />
            <FieldArray name="certifyingBoards">
              {({ push }: FieldArrayRenderProps): ReactElement => (
                <>
                  {values?.certifyingBoards?.map(
                    ({ certifyingBoard, id }: ICertifyingBoardFormModel, idx: number): ReactElement => (
                      <Fragment key={id}>
                        {idx > 0 ? <hr /> : null}
                        <CertifyingBoardForm
                          {...props}
                          boardCreditOptions={filteredBoardCreditOptions}
                          config={boardConfigs[certifyingBoard]}
                          isCertifyingBoardMatchedWithLearnerCallback={(isBoardMatched) => {
                            setCertifyingBoardMatches((previous) => {
                              if (previous[id] === isBoardMatched) return previous;
                              return { ...previous, [id]: isBoardMatched };
                            });
                          }}
                          handleRemoveBoard={() => {
                            const nextCertifyingBoards = values.certifyingBoards.filter((_, index) => index !== idx);
                            const nextValues = {
                              ...values,
                              certifyingBoards: nextCertifyingBoards,
                            };

                            setCertifyingBoardMatches((previous) => {
                              const next = { ...previous };
                              delete next[id];
                              return next;
                            });

                            setFieldValue('certifyingBoards', nextCertifyingBoards, true);
                            validateForm(nextValues);
                          }}
                          hasBoards={!!mocBoardList?.length}
                          hasCollabs={!!remsList?.length}
                          index={idx}
                          /* 4906 - using `matchedLearners.length === 1` instead of `isMatched` here, as it's possible we're not "matched" due to 0/many state licenses, but yet still have exactly one learner to fill in a boardid  */
                          learnerBoards={matchedLearners.length === 1 ? matchedLearners[0].learnerMocBoards : []}
                          mocBoardList={filteredMocList}
                          name="certifyingBoards"
                          remsList={filteredRemsList}
                        />
                      </Fragment>
                    ),
                  )}
                  <div className={classNames('label-text-container', styles.add)}>
                    <Fab
                      disabled={isActivityOnlyCme || isDisabledAddBoards}
                      aria-label="add another instance"
                      color="inherit"
                      size="small"
                      onClick={(): void => {
                        push(generateUniqueCertifyingBoardEntry());
                      }}
                    >
                      <AddIcon color="inherit" />
                    </Fab>
                    <span
                      className={classNames('label-text', styles.text, {
                        [styles.disabled]: isActivityOnlyCme || isDisabledAddBoards,
                      })}
                    >
                      Add a Certifying Board or Collaboration
                    </span>
                  </div>
                </>
              )}
            </FieldArray>
            {/* This Button should always be visible. It is disabled when a learner is not matched correctly */}
            <div className={styles['button-row']}>
              <Button
                disabled={shouldDisableSubmit}
                variant={ButtonVariant.Secondary}
                onClick={submitAndAddAnother}
                title="Submit and Add Another Learner"
                startIcon={isSubmitting && <CircularProgress color="inherit" size="1rem" />}
              >
                Submit and Add Another Learner
              </Button>
            </div>
            {isAllMatchFieldsFailed && !!values?.learnerId && !!values?.medicalSchoolOption && (
              <Grid container>
                <Grid size={{ xs: 12 }}>
                  <div className={classNames('caption-text', styles['error-text'])}>
                    We were unable to locate a learner with the information provided. Please check with the learner and
                    re-submit with updated information.
                  </div>
                </Grid>
              </Grid>
            )}
          </div>
        </div>
        <div className={styles['button-row']}>
          <Button variant={ButtonVariant.Tertiary} onClick={closeWithoutSaving}>
            <KeyboardBackspaceRounded className="tertiary-icon-back" />
            Cancel Without Submitting
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            disabled={shouldDisableSubmit}
            onClick={submitAndClose}
            title="Submit and Close"
            startIcon={isSubmitting && <CircularProgress color="inherit" size="1rem" />}
          >
            Submit and Close
          </Button>
        </div>
      </form>
    </FormikProvider>
  );
};
